import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { searchJobs, getRecommendedJobs } from '../services/jobSearch';
import { JobPosition, JobSearchFilters, UserProfile } from '../types/job';
import { useLanguage } from '../contexts/LanguageContext';
import './JobSearch.css';

export const JobSearch: React.FC = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [jobs, setJobs] = useState<JobPosition[]>([]);
  const [filters, setFilters] = useState<JobSearchFilters>({});
  const [loading, setLoading] = useState(false);
  const [showFilters, setShowFilters] = useState(false);

  // Mock user profile - In production, this would come from user data
  const mockUserProfile: UserProfile = useMemo(() => ({
    skills: ['JavaScript', 'TypeScript', 'React', 'Node.js', 'Python'],
    experience: 5,
    currentPosition: 'Senior Frontend Engineer',
    industry: 'Technology',
    preferredLocations: ['Remote', 'San Francisco'],
    preferredJobTypes: ['remote', 'hybrid'],
  }), []);

  const loadRecommendedJobs = useCallback(() => {
    setLoading(true);
    setTimeout(() => {
      const recommendedJobs = getRecommendedJobs(mockUserProfile);
      setJobs(recommendedJobs);
      setLoading(false);
    }, 500);
  }, [mockUserProfile]);

  useEffect(() => {
    loadRecommendedJobs();
  }, [loadRecommendedJobs]);

  const handleSearch = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);
    setTimeout(() => {
      const results = searchJobs(mockUserProfile, filters);
      setJobs(results);
      setLoading(false);
    }, 500);
  };

  const handleFilterChange = (
    key: keyof JobSearchFilters,
    value: any
  ) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value || undefined,
    }));
  };

  const handleClearFilters = () => {
    setFilters({});
    loadRecommendedJobs();
  };

  return (
    <div className="job-search-container">
      <div className="job-search-header">
        <h1>{t('jobSearch.title')}</h1>
        <p>{t('jobSearch.recommended')}</p>
      </div>

      <div className="job-search-content">
        {/* Search Form */}
        <form onSubmit={handleSearch} className="search-form">
          <div className="search-input-group">
            <input
              type="text"
              placeholder={t('jobSearch.searchJobs')}
              value={filters.keyword || ''}
              onChange={(e) => handleFilterChange('keyword', e.target.value)}
              className="search-input"
            />
            <button type="submit" className="search-button">
              {t('jobSearch.searchJobs')}
            </button>
          </div>

          {/* Filters Toggle */}
          <button
            type="button"
            onClick={() => setShowFilters(!showFilters)}
            className="filters-toggle"
          >
            {t('jobSearch.filters')} {showFilters ? '▲' : '▼'}
          </button>

          {/* Filters Section */}
          {showFilters && (
            <div className="filters-section">
              {/* Job Type Filter */}
              <div className="filter-group">
                <label>{t('jobSearch.jobType')}</label>
                <div className="filter-options">
                  {(['remote', 'onsite', 'hybrid', 'all'] as const).map((type) => (
                    <label key={type} className="checkbox-label">
                      <input
                        type="radio"
                        name="jobType"
                        value={type}
                        checked={filters.jobType === type}
                        onChange={(e) =>
                          handleFilterChange('jobType', e.target.value)
                        }
                      />
                      {t(`jobSearch.${type}`)}
                    </label>
                  ))}
                </div>
              </div>

              {/* Location Filter */}
              <div className="filter-group">
                <label>{t('jobSearch.location')}</label>
                <input
                  type="text"
                  placeholder="Enter location"
                  value={filters.location || ''}
                  onChange={(e) => handleFilterChange('location', e.target.value)}
                  className="filter-input"
                />
              </div>

              {/* Industry Filter */}
              <div className="filter-group">
                <label>{t('jobSearch.industry')}</label>
                <input
                  type="text"
                  placeholder="Enter industry"
                  value={filters.industry || ''}
                  onChange={(e) => handleFilterChange('industry', e.target.value)}
                  className="filter-input"
                />
              </div>

              {/* Clear Filters Button */}
              <button
                type="button"
                onClick={handleClearFilters}
                className="clear-filters-btn"
              >
                Clear All Filters
              </button>
            </div>
          )}
        </form>

        {/* Jobs List */}
        <div className="jobs-list">
          {loading && <div className="loading">Loading jobs...</div>}

          {!loading && jobs.length === 0 && (
            <div className="empty-state">
              <p>{t('jobSearch.noResults')}</p>
            </div>
          )}

          {!loading &&
            jobs.map((job) => (
              <div key={job.id} className="job-card">
                <div className="job-card-header">
                  <div>
                    <h3>{job.title}</h3>
                    <p className="company">{job.company}</p>
                  </div>
                  <div className="match-score">
                    <span className="score-badge">{job.matchScore}%</span>
                    <span className="score-label">{t('jobSearch.basedOn')}</span>
                  </div>
                </div>

                <p className="description">{job.description}</p>

                <div className="job-meta">
                  <span className="meta-item">
                    📍 {job.location}
                  </span>
                  <span className="meta-item">
                    💼 {t(`jobSearch.${job.jobType}`)}
                  </span>
                  {job.salary && (
                    <span className="meta-item">
                      💰 ${job.salary.min.toLocaleString()} - ${job.salary.max.toLocaleString()}
                    </span>
                  )}
                  <span className="meta-item">
                    📚 {job.experience}+ years
                  </span>
                </div>

                <div className="job-skills">
                  <strong>Skills:</strong>
                  <div className="skills-tags">
                    {job.skills.map((skill) => (
                      <span key={skill} className="skill-tag">
                        {skill}
                      </span>
                    ))}
                  </div>
                </div>

                <div className="job-actions">
                  <button className="btn-details">{t('jobSearch.viewDetails')}</button>
                  <button className="btn-apply">{t('jobSearch.applyNow')}</button>
                  <button 
                    className="btn-customize"
                    onClick={() => navigate('/resume-tuner', { state: { jobPosition: job } })}
                    title="Customize your resume for this job"
                  >
                    ✨ Customize CV
                  </button>
                </div>
              </div>
            ))}
        </div>
      </div>
    </div>
  );
};
